import * as React from 'react'
import PropTypes from 'prop-types'
import { useEmblaCarousel } from 'embla-carousel/react'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Media from '@oakwood/oui/Media'
import MediaReveal from '@oakwood/oui/MediaReveal'
import { ASPECT_RATIOS } from 'utils/constants'
import Container from 'components/Container'
import Section from 'components/Section'
import SlideshowNavigation from 'components/SlideshowNavigation'
import Typography from 'components/Typography'

const BREAKPOINT_KEY_UP = 'sm'

export const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: -1,
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  container: {
    display: 'grid',
    overflow: 'hidden',
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gridGap: theme.spacing(6),
    },
  },
  mediaArea: {},
  contentArea: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      paddingBottom: 'var(--coa-toolbar-min-height)',
    },
  },
  emblaRoot: {
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      overflow: 'hidden',
    },
  },
  emblaWrapper: {
    display: 'flex',
    margin: '0 calc(var(--coa-container-spacing) / 2 * -1)',
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      margin: 0,
    },
  },
  emblaSlide: {
    position: 'relative',
    flexShrink: 0,
    width: '100%',
    padding: '0 calc(var(--coa-container-spacing) / 2 * 1)',
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      padding: 0,
    },
  },
  navigation: {
    minHeight: 'var(--coa-toolbar-min-height)',
  },
  heading: {
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: theme.typography.h3,
  },
}))

function ArticleHero(props) {
  const { heading, images, MediaRevealProps, publishDate, subheading, text } = props
  const classes = useStyles(props)

  const [emblaRef, embla] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
  })

  const [prevBtnEnabled, setPrevBtnEnabled] = React.useState(false)
  const [nextBtnEnabled, setNextBtnEnabled] = React.useState(false)
  const [selectedIndex, setSelectedIndex] = React.useState(0)
  const [scrollSnaps, setScrollSnaps] = React.useState([])

  const handlePrev = React.useCallback(() => embla && embla.scrollPrev(), [embla])
  const handleNext = React.useCallback(() => embla && embla.scrollNext(), [embla])

  const handleSelect = React.useCallback(() => {
    setSelectedIndex(embla?.selectedScrollSnap())
    setPrevBtnEnabled(embla?.canScrollPrev())
    setNextBtnEnabled(embla?.canScrollNext())
  }, [embla, setSelectedIndex])

  React.useEffect(() => {
    if (!embla) return
    handleSelect()
    setScrollSnaps(embla.scrollSnapList())
    embla.on('select', handleSelect)
  }, [embla, handleSelect])

  return (
    <Section className={classes.root} gutters="padding">
      <Container className={classes.container}>
        <div className={classes.mediaArea}>
          <div className={classes.emblaRoot} ref={emblaRef}>
            <div className={classes.emblaWrapper}>
              {images?.map((imageSrc, idx) => (
                <div key={idx} className={classes.emblaSlide}>
                  <MediaReveal
                    className={classes.mediaContainer}
                    {...ASPECT_RATIOS.article}
                    {...MediaRevealProps}
                  >
                    <Media className={classes.media} src={imageSrc} />
                  </MediaReveal>
                </div>
              ))}
            </div>
          </div>

          <SlideshowNavigation
            className={classes.navigation}
            listLength={scrollSnaps.length}
            nextButtonDisabled={!nextBtnEnabled}
            onNextClick={handleNext}
            onPrevClick={handlePrev}
            prevButtonDisabled={!prevBtnEnabled}
            selectedIndex={selectedIndex}
          />
        </div>

        <div className={classes.contentArea}>
          {publishDate && (
            <Typography color="textSecondary" variant="caption">
              {publishDate}
            </Typography>
          )}

          <Typography className={classes.heading} component="h1" variant="h4" paragraph>
            {heading}
          </Typography>

          {subheading && (
            <Typography color="textSecondary" variant="caption" paragraph>
              {subheading}
            </Typography>
          )}

          <Typography variant="preamble">{text}</Typography>
        </div>
      </Container>
    </Section>
  )
}

ArticleHero.propTypes = {
  heading: PropTypes.string.isRequired,
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  MediaRevealProps: PropTypes.object,
  publishDate: PropTypes.string,
  subheading: PropTypes.string,
  text: PropTypes.string.isRequired,
}

export default ArticleHero
